import { useEffect, useCallback, useState } from "react";
import "@/App.css";
import confetti from "canvas-confetti";
import { motion, AnimatePresence } from "framer-motion";
import { Toaster } from "sonner";
import { Cake, PartyPopper, Moon, Sun, Sparkles } from "lucide-react";
import FloatingGajras from "@/components/FloatingGajras";
import FallingPetals from "@/components/FallingPetals";
import VideoSection from "@/components/VideoSection";
import CakeSection from "@/components/CakeSection";
import CountdownTimer from "@/components/CountdownTimer";
import NightSky from "@/components/NightSky";
import Constellation from "@/components/Constellation";

const CONFETTI_COLORS = ["#E30B5C", "#FFB6C1", "#FFD700", "#FF69B4", "#ffffff"];

const bigBlast = () => {
  confetti({
    particleCount: 120,
    spread: 100,
    origin: { x: 0.5, y: 0.55 },
    colors: CONFETTI_COLORS,
    scalar: 1.1,
    ticks: 220,
  });
  [0.2, 0.5, 0.8].forEach((x, i) => {
    setTimeout(
      () =>
        confetti({
          particleCount: 70,
          spread: 80,
          origin: { x, y: 0.3 },
          colors: CONFETTI_COLORS,
          gravity: 0.9,
          ticks: 200,
        }),
      200 + i * 250
    );
  });
};

const isBirthdayToday = () => {
  const now = new Date(new Date().toLocaleString("en-US", { timeZone: "Asia/Karachi" }));
  return now.getMonth() === 7 && now.getDate() === 22;
};

const runFinale = () => {
  const end = Date.now() + 7000;
  const frame = () => {
    confetti({
      particleCount: 5,
      angle: 60,
      spread: 70,
      origin: { x: 0 },
      colors: CONFETTI_COLORS,
      shapes: ["star", "circle"],
      scalar: 1.2,
    });
    confetti({
      particleCount: 5,
      angle: 120,
      spread: 70,
      origin: { x: 1 },
      colors: CONFETTI_COLORS,
      shapes: ["star", "circle"],
      scalar: 1.2,
    });
    if (Date.now() < end) requestAnimationFrame(frame);
  };
  frame();
  const bursts = setInterval(() => {
    confetti({
      particleCount: 90,
      spread: 120,
      origin: { x: 0.1 + Math.random() * 0.8, y: 0.1 + Math.random() * 0.4 },
      colors: CONFETTI_COLORS,
      shapes: ["star", "circle"],
      ticks: 220,
      scalar: 1.2,
    });
  }, 450);
  setTimeout(() => clearInterval(bursts), 8000);
};


function App() {
  const [night, setNight] = useState(false);
  const toggleNight = useCallback(() => {
    setNight((n) => {
      if (!n) {
        setTimeout(
          () =>
            document
              .getElementById("constellation-section")
              ?.scrollIntoView({ behavior: "smooth" }),
          500
        );
      }
      return !n;
    });
  }, []);

  const [showFinale, setShowFinale] = useState(false);

  useEffect(() => {
    const forced = new URLSearchParams(window.location.search).has("finale");
    if (isBirthdayToday() || forced) {
      setShowFinale(true);
      return;
    }
    const check = setInterval(() => {
      if (isBirthdayToday()) setShowFinale(true);
    }, 30000);
    return () => clearInterval(check);
  }, []);

  useEffect(() => {
    if (showFinale) runFinale();
  }, [showFinale]);

  useEffect(() => {
    const end = Date.now() + 2000;
    const frame = () => {
      confetti({ particleCount: 4, angle: 60, spread: 60, origin: { x: 0 }, colors: CONFETTI_COLORS });
      confetti({ particleCount: 4, angle: 120, spread: 60, origin: { x: 1 }, colors: CONFETTI_COLORS });
      if (Date.now() < end) requestAnimationFrame(frame);
    };
    frame();
    const bursts = setInterval(() => {
      confetti({
        particleCount: 70,
        spread: 100,
        origin: { x: 0.15 + Math.random() * 0.7, y: 0.15 + Math.random() * 0.35 },
        colors: CONFETTI_COLORS,
        ticks: 200,
        gravity: 0.85,
      });
    }, 850);
    const stop = setTimeout(() => clearInterval(bursts), 4200);
    return () => {
      clearInterval(bursts);
      clearTimeout(stop);
    };
  }, []);

  const celebrate = useCallback(() => bigBlast(), []);

  return (
    <div
      className={`min-h-screen transition-colors duration-700 ${
        night ? "bg-[#0B1026] text-white" : "bg-[#FFFDF7] text-[#2C1820]"
      }`}
    >
      {night && <NightSky />}
      <FloatingGajras />
      <FallingPetals />

      <motion.button
        data-testid="night-mode-toggle"
        onClick={toggleNight}
        animate={{
          scale: [1, 1.06, 1],
          boxShadow: [
            "0 0 12px 2px rgba(227,11,92,0.45)",
            "0 0 30px 10px rgba(255,215,0,0.65)",
            "0 0 12px 2px rgba(227,11,92,0.45)",
          ],
        }}
        transition={{ duration: 1.6, repeat: Infinity, ease: "easeInOut" }}
        className="fixed top-5 right-5 z-50 rounded-full bg-[#E30B5C] text-white font-semibold px-6 py-3 flex items-center gap-2 hover:bg-[#BE0032] transition-colors duration-300"
      >
        {night ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
        {night ? "Day Mode" : "Click Here"}
      </motion.button>

      <header className="relative z-10 min-h-screen flex flex-col items-center justify-center text-center px-6 py-24">
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="flex items-center gap-2 rounded-full border border-[#FFB6C1] bg-white/70 backdrop-blur-md px-6 py-2.5 shadow-[0_4px_16px_rgba(227,11,92,0.12)]"
          data-testid="date-badge"
        >
          <Cake className="w-4 h-4 text-[#E30B5C]" />
          <span className="text-sm uppercase tracking-[0.2em] font-medium text-[#E30B5C]">
            22 August
          </span>
        </motion.div>

        <motion.h1
          data-testid="birthday-heading"
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.8, delay: 0.2, ease: "easeOut" }}
          className="mt-2 text-6xl sm:text-8xl lg:text-9xl leading-none text-[#E30B5C] drop-shadow-[0_4px_24px_rgba(227,11,92,0.25)]"
          style={{ fontFamily: "'Caveat', cursive" }}
        >
          Happy Birthday
          <span
            className={`block mt-2 transition-colors duration-700 ${
              night ? "text-white" : "text-[#2C1820]"
            }`}
          >
            Noor Fatima
          </span>
        </motion.h1>

        <motion.p
          data-testid="urdu-name"
          lang="ur"
          dir="rtl"
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.35 }}
          className="mt-4 text-2xl sm:text-3xl text-[#D4A017] drop-shadow-[0_2px_12px_rgba(212,160,23,0.35)]"
          style={{ fontFamily: "'Noto Nastaliq Urdu', serif", lineHeight: 2 }}
        >
          نور فاطمہ
        </motion.p>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.5 }}
          className={`mt-8 max-w-xl text-lg sm:text-xl leading-relaxed transition-colors duration-700 ${
            night ? "text-[#D9C9E0]" : "text-[#5C404C]"
          }`}
          data-testid="birthday-message"
        >
          aap hamesha khush rahein. Today, tomorrow and always.
        </motion.p>

        <CountdownTimer />

        <motion.button
          data-testid="celebrate-button"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.75 }}
          onClick={celebrate}
          className="mt-12 rounded-full bg-[#E30B5C] text-white font-semibold text-lg px-10 py-4 flex items-center gap-3 shadow-[0_8px_24px_rgba(227,11,92,0.4)] hover:bg-[#BE0032] hover:-translate-y-1 hover:scale-105 transition-[background-color,transform] duration-300 ease-out"
        >
          <PartyPopper className="w-5 h-5" /> Celebrate Noor!
        </motion.button>
      </header>

      <CakeSection />

      <VideoSection />

      {night && <Constellation />}

      <AnimatePresence>
        {showFinale && (
          <motion.div
            data-testid="birthday-finale-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[60] flex items-center justify-center bg-[#0B1026]/70 backdrop-blur-md px-6"
          >
            <div className="text-center">
              <motion.p
                initial={{ scale: 0.7, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ duration: 0.8, ease: "easeOut" }}
                className="text-6xl sm:text-8xl text-[#FFD700] drop-shadow-[0_4px_24px_rgba(255,215,0,0.4)]"
                style={{ fontFamily: "'Caveat', cursive" }}
              >
                tem hogyaa
              </motion.p>
              <p className="mt-6 text-white/90 text-lg sm:text-xl">
                August 22 — the day the world got its brightest light
              </p>
              <button
                data-testid="finale-close-button"
                onClick={() => setShowFinale(false)}
                className="mt-10 rounded-full bg-[#E30B5C] text-white font-semibold px-10 py-4 inline-flex items-center gap-3 shadow-[0_8px_24px_rgba(227,11,92,0.4)] hover:bg-[#BE0032] hover:-translate-y-1 transition-[background-color,transform] duration-300 ease-out"
              >
                <Sparkles className="w-5 h-5" /> Keep celebrating
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <Toaster position="top-center" richColors />
    </div>
  );
}

export default App;
